import React from 'react';

import { useCompleteModal } from 'modal.react.js';
import { CloudArrowUp, Trash } from 'phosphor-react';

import { Alert } from 'components/shared/Alert';
import { AModal } from 'components/shared/AModal';
import { IDropZoneFiles } from 'components/shared/Assets/shared/Form/types';
import Button from 'components/shared/Button';
import FileDropZone from 'components/shared/FileDropZone';
import Loader from 'components/shared/Loader';
import Text from 'components/shared/Typography/Text';
import { DOCUMENT_MIME_TYPES } from 'utils/asset';

import s from './index.module.css';

type Props = {
  onSubmit: (file: IDropZoneFiles, callback: () => void) => void;
};

export const MagicImportUploadModal = (props: Props) => {
  const modalControl = useCompleteModal();
  const [isLoading, setIsLoading] = React.useState(false);
  const [file, setFile] = React.useState<IDropZoneFiles | null>(null);

  const closeModal = () => {
    modalControl.success(null);
  };

  const submitForm = async () => {
    try {
      setIsLoading(true);
      props.onSubmit(file!, () => {
        closeModal();
      });
    } catch (e) {
      console.error('---- submitForm e', e);
      setIsLoading(false);
    }
  };

  return (
    <AModal
      header="Magic Import"
      isOpen={true}
      footer={<MagicImportModalFooter isValid={file !== null} submitForm={submitForm} isLoading={isLoading} />}
      size="xlg"
      onClose={closeModal}
      isCloseOnEsc
      contentClassName={s.modalContent}
    >
      <div className={s.betaFeatureText}>
        <Text type="brandon" size="medium">
          Once you select a file and click “Import,” the system will begin analyzing the file and inserting the textual
          content into your itinerary. This process usually takes 1–5 minutes.
        </Text>
      </div>

      {file ? (
        <div className={s.fileWrapper}>
          <Text type="brandon" size="medium" className={s.fileName}>
            {file.filename}
          </Text>

          <Trash className={s.trashIcon} onClick={() => setFile(null)} />
        </div>
      ) : (
        <div className={s.dropZoneWrapper}>
          <FileDropZone
            descriptionVariant="default"
            placeholder="Drop your file or click to browse"
            allowedMimeTypes={DOCUMENT_MIME_TYPES}
            maxFileSize={50}
            recommendedFileSize={50}
            onSelectNewFiles={(files: IDropZoneFiles[]) => {
              if (!files?.[0]) return;

              setFile(files[0]);
            }}
            headComponent={<CloudArrowUp className={s.fileDropZoneIcon} />}
            needToShowWarning={false}
            multiple={false}
          />
        </div>
      )}

      <Alert
        variant="info"
        content={[
          <>
            <Text type="brandon" size="medium" className={s.betaFeature}>
              Beta Feature:
            </Text>{' '}
            Try it out and share your thoughts via{' '}
            <a
              href="https://safariportal.typeform.com/magic"
              target="_blank"
              rel="noopener noreferrer"
              className="link"
              style={{ textDecoration: 'underline' }}
            >
              this form
            </a>{' '}
            so we can keep improving this feature.
          </>,
        ]}
        className={s.alert}
      />
    </AModal>
  );
};

const MagicImportModalFooter = (props: { isValid: boolean; submitForm: () => void; isLoading: boolean }) => {
  return (
    <div>
      <Button
        onClick={props.submitForm}
        disabled={!props.isValid || props.isLoading}
        type="primary"
        size="medium"
        htmlType="button"
      >
        <div className={s.submitButton}>
          <Text type="brandon" color="white">
            Import
          </Text>
          {props.isLoading && <Loader width="1.6rem" height="1.6rem" />}
        </div>
      </Button>
    </div>
  );
};
